import React, { useEffect, useState } from 'react';
import { Row, Col, Spin } from 'antd';
import {
    TeamOutlined, CheckCircleOutlined, SafetyCertificateOutlined,
    CreditCardOutlined, RiseOutlined, CalendarOutlined,
    UserAddOutlined, WarningOutlined, LoadingOutlined, MessageOutlined,
} from '@ant-design/icons';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell,
} from 'recharts';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const statConfig = [
    { key: 'total_users',           label: 'Total Users',           icon: <TeamOutlined />,              color: '#AD1457', bg: '#FCE4EC' },
    { key: 'online_users',          label: 'Online Users',          icon: <CheckCircleOutlined />,       color: '#10B981', bg: '#D1FAE5' },
    { key: 'pending_verifications', label: 'Pending Verifications', icon: <SafetyCertificateOutlined />, color: '#F59E0B', bg: '#FEF3C7' },
    { key: 'active_subscriptions',  label: 'Active Subscriptions',  icon: <CreditCardOutlined />,        color: '#3B82F6', bg: '#DBEAFE' },
    { key: 'total_revenue',         label: 'Total Revenue',         icon: <RiseOutlined />,              color: '#8B5CF6', bg: '#EDE9FE', prefix: '₹' },
    { key: 'revenue_this_month',    label: 'Revenue This Month',    icon: <CalendarOutlined />,          color: '#AD1457', bg: '#FCE4EC', prefix: '₹' },
    { key: 'new_users_this_month',  label: 'New Users (Month)',     icon: <UserAddOutlined />,           color: '#10B981', bg: '#D1FAE5' },
    { key: 'flagged_users',         label: 'Flagged Users',         icon: <WarningOutlined />,           color: '#EF4444', bg: '#FEE2E2' },
    { key: 'total_enquiries',       label: 'Total Enquiries',       icon: <MessageOutlined />,           color: '#0F766E', bg: '#CCFBF1' },
];

function formatValue(val, prefix) {
    if (prefix === '₹') return `₹${Number(val).toLocaleString('en-IN')}`;
    return Number(val).toLocaleString('en-IN');
}

function formatYAxis(val) {
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000)   return `₹${(val / 1000).toFixed(0)}K`;
    return `₹${val}`;
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: '#1E293B', color: '#fff', borderRadius: 10,
            padding: '10px 16px', fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>
            <div style={{ color: '#94A3B8', marginBottom: 4, fontSize: 12 }}>{label}</div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
                ₹{Number(payload[0].value).toLocaleString('en-IN')}
            </div>
        </div>
    );
};

function UserBreakdownChart({ data }) {
    if (!data) return null;

    const subscribed = data.active_subscriptions;
    const activeOnly = Math.max(data.online_users - data.active_subscriptions, 0);
    const inactive   = Math.max(data.total_users - data.online_users, 0);
    const total      = data.total_users || 1;

    const segments = [
        { name: 'Subscribed',      value: subscribed, color: '#3B82F6' },
        { name: 'Online',          value: activeOnly,  color: '#10B981' },
        { name: 'Inactive',        value: inactive,    color: '#E2E8F0' },
    ].filter((s) => s.value > 0);

    const CustomDonutTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const { name, value } = payload[0].payload;
        return (
            <div style={{
                background: '#1E293B', color: '#fff', borderRadius: 8,
                padding: '8px 14px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}>
                <div style={{ color: '#94A3B8', marginBottom: 2 }}>{name}</div>
                <div style={{ fontWeight: 700 }}>
                    {value} <span style={{ color: '#94A3B8', fontWeight: 400 }}>({Math.round((value / total) * 100)}%)</span>
                </div>
            </div>
        );
    };

    return (
        <>
            <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                    <Pie
                        data={segments}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        strokeWidth={0}
                    >
                        {segments.map((s) => (
                            <Cell key={s.name} fill={s.color} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomDonutTooltip />} />
                </PieChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 8 }}>
                {segments.map((s) => (
                    <div key={s.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                            <span style={{ fontSize: 13, color: '#64748B' }}>{s.name}</span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                            {s.value} <span style={{ color: '#94A3B8', fontWeight: 400, fontSize: 12 }}>({Math.round((s.value / total) * 100)}%)</span>
                        </span>
                    </div>
                ))}
            </div>
        </>
    );
}

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.get('/admin/dashboard')
            .then((res) => setData(res.data.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const revenueByMonth = (data?.revenue_by_month || []).map((m) => ({
        month: MONTH_LABELS[parseInt(m.month.slice(5, 7), 10) - 1] + ' ' + m.month.slice(2, 4),
        revenue: parseFloat(m.revenue) || 0,
    }));

    return (
        <AdminLayout title="Dashboard">
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
                </div>
            ) : (
                <>
                    <div className="admin-page-header">
                        <h1 className="admin-page-title">Dashboard Overview</h1>
                        <p className="admin-page-subtitle">Platform metrics and activity summary</p>
                    </div>

                    {/* Stat cards */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        {statConfig.map((s) => (
                            <Col key={s.key} xs={24} sm={12} lg={6}>
                                <div className="admin-stat-card">
                                    <div className="admin-stat-card-icon" style={{ background: s.bg, color: s.color }}>
                                        {s.icon}
                                    </div>
                                    <div className="admin-stat-card-value">
                                        {data ? formatValue(data[s.key], s.prefix) : '—'}
                                    </div>
                                    <div className="admin-stat-card-label">{s.label}</div>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {/* Revenue chart + quick stats */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={16}>
                            <div className="admin-card">
                                <div className="admin-card-header">
                                    <h3 className="admin-card-title">Revenue — Last 12 Months</h3>
                                </div>
                                <div className="admin-card-body">
                                    {revenueByMonth.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--color-text-muted)' }}>
                                            No revenue data available
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={260}>
                                            <AreaChart data={revenueByMonth} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%"  stopColor="#AD1457" stopOpacity={0.2} />
                                                        <stop offset="95%" stopColor="#AD1457" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fontSize: 12, fill: '#94A3B8' }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    tickFormatter={formatYAxis}
                                                    tick={{ fontSize: 12, fill: '#94A3B8' }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    width={58}
                                                />
                                                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#AD1457', strokeWidth: 1, strokeDasharray: '4 3' }} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="revenue"
                                                    stroke="#AD1457"
                                                    strokeWidth={2.5}
                                                    fill="url(#revenueGrad)"
                                                    dot={false}
                                                    activeDot={{ r: 5, fill: '#AD1457', stroke: '#fff', strokeWidth: 2 }}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>
                        </Col>

                        <Col xs={24} lg={8}>
                            <div className="admin-card" style={{ height: '100%' }}>
                                <div className="admin-card-header">
                                    <h3 className="admin-card-title">User Breakdown</h3>
                                </div>
                                <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <UserBreakdownChart data={data} />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </AdminLayout>
    );
}
